<template>
  <PageLayout>
    <template v-slot:header>
      <div class="d-flex m-3 mx-lg-0 breadcrumb-box">
        <breadcrumb name="user-profile" :list="breadcrumbList" />
      </div>
    </template>
    <template slot="main">
      <b-card>
        <b-form @submit.prevent="save">
          <b-row>
            <b-col cols="12" xl="3" class="user-image">
              <b-card :title="userFullName" :sub-title="user.companyName">
                <template v-slot:header>
                  <i class="material-icons">account_circle</i>
                </template>
              </b-card>
            </b-col>
            <b-col cols="12" xl="9">
              <b-nav class="link">
                <b-nav-item key="informations" :active="true">
                  {{ resources.page.home.users.personalSettings.informations }}
                </b-nav-item>
              </b-nav>
              <b-row class="mt-3">
                <b-col cols="12" xl="6">
                  <CustomInput
                    :label="resources.general.input.firstName"
                    :title="resources.general.input.textWarning"
                    v-model="user.profile.firstname"
                    :labelWrong="resources.general.input.firstNameWrong"
                  >
                  </CustomInput>
                </b-col>
                <b-col cols="12" xl="6">
                  <CustomInput
                    :label="resources.general.input.lastName"
                    :title="resources.general.input.textWarning"
                    v-model="user.profile.lastname"
                    :labelWrong="resources.general.input.firstNameWrong"
                  >
                  </CustomInput>
                </b-col>
                <b-col cols="12" xl="6">
                  <CustomEmail
                    :label="resources.general.input.emailTitle"
                    :initialValue="user.email"
                    :disabled="true"
                    inputId="email"
                  />
                </b-col>
                <b-col cols="12" xl="6">
                  <b-form-group>
                    <b-form-select size="lg" required v-model="user.role">
                      <option value="admin" v-if="user.role === 'admin'">
                        {{ resources.general.input.roles.admin }}
                      </option>
                      <option value="manager" v-if="user.role === 'manager'">
                        {{ resources.general.input.roles.manager }}
                      </option>
                      <option value="user" v-if="user.role === 'user'">
                        {{ resources.general.input.roles.user }}
                      </option>
                    </b-form-select>
                    <label class="custom-input-label">
                      {{ resources.general.input.role }}
                    </label>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="12" xl="6">
                  <b-link
                    id="addUsersForm"
                    class="link text-primary"
                    @click="openModalChangePassword"
                  >
                    {{
                      resources.page.home.users.personalSettings.changePassword
                    }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-list-group flush class="mt-4">
            <b-list-group-item>
              <b-row align-h="end">
                <b-button
                  style="max-width:200px"
                  id="btn-save"
                  v-on:keyup.enter="submit"
                  :disabled="saving"
                  type="submit"
                  variant="primary"
                  class="col-12 mt-3"
                >
                  <i
                    class="mdi mdi-spin pr-2"
                    v-bind:class="{ 'mdi-loading': saving }"
                  >
                  </i>
                  {{ resources.page.home.users.edit.save }}
                </b-button>
              </b-row>
            </b-list-group-item>
          </b-list-group>
        </b-form>
        <b-modal
          ref="modalChangePassword"
          :title="resources.page.home.users.personalSettings.changePassword"
          :ok-title="resources.page.home.users.edit.save"
          ok-variant="primary"
          @ok="savePassword"
          header-bg-variant="light"
          hide-footer
        >
          <div v-if="loading" class="loading">
            <loading />
            <p>{{ resources.page.login.new.confirmEmailPage.loading }}</p>
          </div>
          <b-form v-else @submit.prevent="savePassword" ref="form">
            <CustomInput
              name="currentPassword"
              type="password"
              :title="resources.general.input.passwordWarning"
              :label="resources.page.home.personalSettings.currentPassword"
              :labelWrong="resources.page.home.personalSettings.currentPassword"
              :disablePasswordVerifications="true"
              v-on:update="updatePassword('current', $event.value)"
            >
            </CustomInput>
            <CustomInput
              name="newPassword"
              type="password"
              hasPasswordConfirm="true"
              :passwordRules="passwordRules"
              :title="resources.page.home.personalSettings.newPassword"
              :label="resources.page.home.personalSettings.newPassword"
              :labelWrong="resources.page.home.personalSettings.newPassword"
              :confirmTitle="resources.general.input.passwordConfirmWarning"
              :confirmLabel="
                resources.page.home.personalSettings.newPasswordConfirmation
              "
              :confirmLabelWrong="resources.general.input.passwordConfirmWrong"
              v-on:update="updatePassword('new', $event.value)"
            >
            </CustomInput>
            <footer class="text-right">
              <b-button
                class="btn-save-pass"
                variant="primary"
                type="submit"
                :disabled="submitInProgress"
              >
                <i
                  class="mdi mdi-spin pr-2"
                  :class="{ 'mdi-loading': submitInProgress }"
                >
                </i>
                {{ resources.page.home.users.edit.save }}
              </b-button>
            </footer>
          </b-form>
        </b-modal>
      </b-card>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import CustomEmail from '@/components/Shared/Form/CustomEmail.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import { getUser } from '@/service/user'
import { HIGH } from '@/helpers/messages-priority'
import Loading from '@/components/Shared/Loading.vue'

export default {
  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'users/profile' })
  },
  components: {
    CustomInput,
    CustomEmail,
    PageLayout,
    Breadcrumb,
    Loading,
  },
  data() {
    return {
      loading: false,
      passwordRules: undefined,
      password: '',
      currentPassword: '',
      newPassword: '',
      user: {},
      userFullName: '',
      saving: false,
      submitInProgress: false,
    }
  },
  created() {
    this.user = getUser()
    this.userFullName = `${this.user.profile.firstname} ${this.user.profile.lastname}`
  },
  computed: {
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.personalSettings,
        },
      ]
    },
    ...mapGetters({
      resources: 'resources',
    }),
  },
  methods: {
    updatePassword(type, password) {
      this.password = ''
      this[`${type}Password`] = password
    },
    openModalChangePassword() {
      this.currentPassword = ''
      this.newPassword = ''
      this.$refs.modalChangePassword.show()

      this.loading = true
      this.$nextTick(() => {
        this.loading = true
        this.$store
          .dispatch('user/loadPasswordRules', {
            token: btoa(this.user.tenantName),
            action: 'reset-password',
          })
          .then(({ data: { passwordRules } }) => {
            this.passwordRules = passwordRules
            this.loading = false
          })
          .catch(() => {
            this.loading = false
          })
      })
    },
    savePassword() {
      this.submitInProgress = true
      this.$store
        .dispatch('users/changePassword', {
          currentPassword: this.currentPassword,
          newPassword: this.newPassword,
        })
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.page.home.users.edit.saveWithSuccessMessage,
            status: 'success',
          })
        })
        .catch(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.page.home.users.edit.errorMessage,
            status: 'danger',
          })
        })
        .finally(() => {
          this.submitInProgress = false
          this.$refs.modalChangePassword.hide()
        })
    },
    save() {
      this.saving = true
      this.$store
        .dispatch('users/updateMe', this.user)
        .then(() => {
          this.saving = false
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.page.home.users.edit.saveWithSuccessMessage,
            status: 'success',
          })
          this.$router.push({ name: 'dashboard' })
        })
        .catch(() => {
          this.saving = false
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.page.home.users.edit.errorMessage,
            status: 'danger',
          })
        })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.loading {
  margin: 0 auto;
  text-align: center;
}

.btn-save-pass {
  margin-top: 25px;
  min-width: 150px;
}

.link {
  margin-bottom: 30px;
}

.user-image {
  margin-bottom: 30px;
  text-align: center;
  .card-header {
    justify-content: center;
    i {
      color: map-get($theme-colors, 'primary');
      margin-top: 10px;
      font-size: 72pt;
    }
  }
}
</style>
